/**
 * PortfolioPage - Portfolio analysis dashboard.
 *
 * Full-width dashboard layout with:
 * 1. Staleness/Disclaimer banners (full-width)
 * 2. Hero section (full-width)
 * 3. Scan progress / Needs Attention (full-width, conditional)
 * 4. Summary Cards (full-width)
 * 5. Dashboard grid: Allocation + Sectors (left) | Performance Chart (right)
 * 6. Dashboard grid: Portfolio News (left) | Market Movers (right)
 * 7. Holdings Table (full-width)
 * 8. Deep Dive drawer + minimized pill
 *
 * Three states: Empty -> Loading -> Dashboard
 * Auto-scans on upload; loads cached results on return.
 * Auto-refreshes prices on page load for accurate P&L.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { RefreshCw, Loader2, BarChart3, Upload } from 'lucide-react';
import { usePageContextUpdater } from '@/contexts/PageContext';
import { cn } from '@/lib/utils';
import { API_BASE_URL } from '@/lib/api';
import { usePortfolio } from '@/hooks/usePortfolio';
import { usePortfolioScan } from '@/hooks/usePortfolioScan';
import { usePortfolioSectors } from '@/hooks/usePortfolioSectors';
import { usePortfolioNews } from '@/hooks/usePortfolioNews';
import { getMarketStatus } from '@/utils/marketHours';
import { PortfolioUpload } from '@/components/portfolio/PortfolioUpload';
import { PortfolioSummaryCard } from '@/components/portfolio/PortfolioSummaryCard';
import { PortfolioChart } from '@/components/portfolio/PortfolioChart';
import { AllocationChart } from '@/components/portfolio/AllocationChart';
import { SectorBreakdown } from '@/components/portfolio/SectorBreakdown';
import { PortfolioNewsFeed } from '@/components/portfolio/PortfolioNewsFeed';
import { TopMovers } from '@/components/portfolio/TopMovers';
import { HoldingsTable } from '@/components/portfolio/HoldingsTable';
import { PortfolioDisclaimer } from '@/components/portfolio/PortfolioDisclaimer';
import { ScanProgressBar } from '@/components/portfolio/ScanProgressBar';
import { NeedsAttention } from '@/components/portfolio/NeedsAttention';
import { DeepDiveDrawer } from '@/components/portfolio/DeepDiveDrawer';
import { DeepDiveMinimized } from '@/components/portfolio/DeepDiveMinimized';
import { DeepDiveHistory } from '@/components/portfolio/DeepDiveHistory';
import type { DrawerMode, AnalysisPhase } from '@/components/portfolio/DeepDiveDrawer';
import type { PositionWithScan, Stance, ScanResult } from '@/types/portfolio';

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
}

function isNeedsAttention(scan: ScanResult | undefined, gainLoss: number): boolean {
  if (!scan) return false;
  const bearishStances: Stance[] = ['BEARISH', 'LEANING_BEARISH'];
  if (bearishStances.includes(scan.stance) && gainLoss < 0) return true;
  if (scan.bear_risk > 75) return true;
  return false;
}

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('access_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

function getTimeSince(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function PortfolioPage() {
  const {
    positions,
    summary,
    importHistory,
    staleness,
    isLoading,
    isUploading,
    isRefreshingPrices,
    lastRefreshed,
    error,
    uploadFile,
    refreshPrices,
  } = usePortfolio();

  const {
    scanResults,
    scanStatus,
    progress,
    error: scanError,
    startScan,
    loadCachedResults,
  } = usePortfolioScan();

  const hasPositions = positions.length > 0;

  // Dashboard supplement hooks
  const { sectors, isLoading: isLoadingSectors } = usePortfolioSectors(hasPositions);
  const { news: portfolioNews, isLoading: isLoadingNews } = usePortfolioNews(hasPositions);

  const [sparklines, setSparklines] = useState<Record<string, number[]>>({});
  const hasFetchedCacheRef = useRef(false);

  // Deep dive state
  const [deepDiveTicker, setDeepDiveTicker] = useState<string | null>(null);
  const [drawerMode, setDrawerMode] = useState<DrawerMode>('closed');
  const [analysisPhase, setAnalysisPhase] = useState<AnalysisPhase>('idle');
  const [savedViewResult, setSavedViewResult] = useState<{ killSheet: any; snapshot: any } | null>(null);

  const lastImport = importHistory[0];
  const marketStatus = getMarketStatus();

  // Expose page context for the debate panel
  const { setPageContext, clearPageContext } = usePageContextUpdater();
  useEffect(() => {
    if (!hasPositions || !summary) return;
    const topHoldings = [...positions]
      .sort((a, b) => b.current_value - a.current_value)
      .slice(0, 5)
      .map(p => p.symbol);
    setPageContext({
      pageType: 'portfolio',
      tickers: positions.map(p => p.symbol),
      portfolioSummary: {
        totalValue: summary.total_value,
        positionCount: summary.position_count,
        topHoldings,
      },
    });
    return () => clearPageContext();
  }, [positions, summary, hasPositions, setPageContext, clearPageContext]);

  // Load cached scan results on mount (for returning users)
  useEffect(() => {
    if (hasPositions && !hasFetchedCacheRef.current) {
      hasFetchedCacheRef.current = true;
      loadCachedResults();
    }
  }, [hasPositions, loadCachedResults]);

  // Fetch sparkline data when positions change
  useEffect(() => {
    if (!hasPositions) return;
    const tickers = positions.map((p) => p.symbol).join(',');
    if (!tickers) return;

    const token = localStorage.getItem('access_token');
    fetch(`${API_BASE_URL}/api/v1/portfolio/scan/sparklines/batch?tickers=${tickers}&days=30`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.sparklines) setSparklines(data.sparklines);
      })
      .catch(() => {});
  }, [positions, hasPositions]);

  // Handle upload: triggers scan automatically
  const handleUpload = useCallback(
    async (file: File) => {
      const result = await uploadFile(file);
      if (result?.success) {
        setTimeout(() => startScan(), 500);
      }
      return result;
    },
    [uploadFile, startScan]
  );

  // Deep dive handlers
  const handleStartDeepDive = useCallback((symbol: string) => {
    if (analysisPhase === 'running') return; // Block while running
    setSavedViewResult(null);
    setDeepDiveTicker(symbol);
    setDrawerMode('open');
    setAnalysisPhase('idle');
  }, [analysisPhase]);

  const handleMinimize = useCallback(() => {
    setDrawerMode('minimized');
  }, []);

  const handleExpandFromMinimized = useCallback(() => {
    setDrawerMode('open');
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setDrawerMode('closed');
    setDeepDiveTicker(null);
    setAnalysisPhase('idle');
    setSavedViewResult(null);
  }, []);

  const handleAnalysisComplete = useCallback((killSheet: any, snapshot: any) => {
    // Auto-save to server
    if (!deepDiveTicker) return;
    const headers = getAuthHeaders();
    fetch(`${API_BASE_URL}/api/v1/portfolio/deep-dive/save`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        ticker: deepDiveTicker,
        kill_sheet: killSheet,
        snapshot: snapshot,
        winning_side: killSheet?.winner?.side || killSheet?.winningSide,
        confidence: killSheet?.winner?.confidence || killSheet?.confidence,
      }),
    }).catch(() => {}); // Fire and forget
  }, [deepDiveTicker]);

  const handleViewHistoricalResult = useCallback(async (resultId: number) => {
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/v1/portfolio/deep-dive/${resultId}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setDeepDiveTicker(data.ticker);
        setSavedViewResult({ killSheet: data.kill_sheet, snapshot: data.snapshot });
        setDrawerMode('open');
        setAnalysisPhase('done');
      }
    } catch {
      // Silent fail
    }
  }, []);

  // Merge positions with scan results
  const positionsWithScan: PositionWithScan[] = useMemo(() => {
    return positions.map((pos) => {
      const scan = scanResults.get(pos.symbol.toUpperCase());
      const scanStatusForPos = scan
        ? scan.cached ? 'cached' as const : 'complete' as const
        : scanStatus === 'scanning' ? 'analyzing' as const : 'idle' as const;

      return {
        ...pos,
        scan,
        scanStatus: scanStatusForPos,
        needsAttention: isNeedsAttention(scan, pos.total_gain_loss),
      };
    });
  }, [positions, scanResults, scanStatus]);

  const deepDivePosition = positions.find(
    (p) => p.symbol === deepDiveTicker
  );

  // Loading State
  if (isLoading && !hasPositions) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
        <p className="mt-3 text-text-muted">Loading portfolio...</p>
      </div>
    );
  }

  // Empty State
  if (!isLoading && !hasPositions) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center">
        <div className="max-w-lg w-full">
          <h1 className="text-2xl font-bold text-text-primary mb-2 text-center">Portfolio</h1>
          <p className="text-text-muted text-center mb-8">Upload your holdings to get started</p>
          <PortfolioUpload onUpload={handleUpload} isUploading={isUploading} />
        </div>
      </div>
    );
  }

  const hasScanResults = scanResults.size > 0;
  const isTodayPositive = summary ? summary.today_gain >= 0 : true;
  const isScanning = scanStatus === 'scanning';

  return (
    <div className="-mx-4 md:-mx-6 lg:-mx-8 -mt-6 min-h-[calc(100vh-3rem)]">
      <div className="px-4 md:px-6 lg:px-8 py-6 space-y-6">

        {/* Staleness Banner */}
        {staleness?.is_stale && staleness.days_since_import && staleness.days_since_import > 1 && (
          <div className="flex items-center justify-between p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <span className="text-sm text-amber-400">
              Portfolio data is {staleness.days_since_import} day(s) old. Upload a new export for accurate analysis.
            </span>
            <label className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 transition-colors cursor-pointer">
              <Upload className="w-3.5 h-3.5" />
              Upload CSV
              <input
                type="file"
                accept=".csv,.pdf"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleUpload(file);
                }}
              />
            </label>
          </div>
        )}

        {/* Disclaimer */}
        {hasScanResults && <PortfolioDisclaimer />}

        {/* Error States */}
        {error && (
          <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
            {error}
          </div>
        )}
        {scanError && (
          <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
            Scan error: {scanError}
          </div>
        )}

        {/* Hero Section */}
        <div className="flex items-end justify-between">
          <div>
            <p className="text-xs text-text-muted uppercase tracking-wider mb-1">Total Portfolio Value</p>
            <h1 className="text-4xl md:text-5xl font-bold text-text-primary tabular-nums">
              {summary ? formatCurrency(summary.total_value) : '$—'}
            </h1>
            <div className="flex items-center gap-3 mt-2">
              {summary && (
                <span className={cn(
                  'text-sm font-medium px-2 py-0.5 rounded-md',
                  isTodayPositive
                    ? 'text-green-400 bg-green-400/10'
                    : 'text-red-400 bg-red-400/10'
                )}>
                  {isTodayPositive ? '+' : ''}{formatCurrency(summary.today_gain)} today
                </span>
              )}
              <span className="text-sm text-text-muted">
                {positions.length} position{positions.length !== 1 ? 's' : ''}
              </span>
              {/* Market status */}
              <span className={cn(
                'text-[10px] font-medium px-1.5 py-0.5 rounded',
                marketStatus === 'open' ? 'bg-green-500/10 text-green-400' :
                marketStatus === 'pre-market' ? 'bg-amber-500/10 text-amber-400' :
                marketStatus === 'after-hours' ? 'bg-blue-500/10 text-blue-400' :
                'bg-surface-light text-text-secondary'
              )}>
                {marketStatus === 'open' ? 'Market Open' :
                 marketStatus === 'pre-market' ? 'Pre-Market' :
                 marketStatus === 'after-hours' ? 'After Hours' :
                 'Market Closed'}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Price refresh status */}
            <div className="flex flex-col items-end gap-0.5">
              {lastRefreshed && (
                <span className="text-[10px] text-text-muted">
                  Prices: {getTimeSince(lastRefreshed)}
                </span>
              )}
              {lastImport && (
                <span className="text-[10px] text-text-muted hidden sm:inline">
                  Import: {new Date(lastImport.import_date).toLocaleDateString()}
                </span>
              )}
            </div>

            {/* Update Holdings button */}
            <label className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-text-muted hover:text-text-secondary hover:bg-surface-light transition-colors cursor-pointer">
              <Upload className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Update</span>
              <input
                type="file"
                accept=".csv,.pdf"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleUpload(file);
                }}
              />
            </label>

            {/* Deep dive history */}
            <DeepDiveHistory onViewResult={handleViewHistoricalResult} />

            {!isScanning && hasPositions && (
              <button
                onClick={() => startScan()}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-primary bg-primary/10 hover:bg-primary/20 transition-colors"
              >
                <BarChart3 className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Analyze All</span>
              </button>
            )}
            <button
              onClick={isRefreshingPrices ? undefined : refreshPrices}
              disabled={isRefreshingPrices}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-text-secondary hover:text-text-primary hover:bg-surface-light transition-colors"
            >
              <RefreshCw className={cn('w-4 h-4', (isLoading || isRefreshingPrices) && 'animate-spin')} />
              <span className="hidden sm:inline">{isRefreshingPrices ? 'Refreshing' : 'Refresh'}</span>
            </button>
          </div>
        </div>

        {/* Scan Progress */}
        {isScanning && (
          <ScanProgressBar progress={progress} />
        )}

        {/* Needs Attention */}
        {!isScanning && hasScanResults && (
          <NeedsAttention
            positions={positionsWithScan}
            onDeepDive={handleStartDeepDive}
          />
        )}

        {/* Summary Cards */}
        {summary && <PortfolioSummaryCard summary={summary} />}

        {/* Dashboard Grid: Charts & Insights */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left column: Allocation + Sectors */}
          <div className="lg:col-span-5 space-y-6">
            <AllocationChart positions={positions} />
            <SectorBreakdown sectors={sectors} isLoading={isLoadingSectors} />
          </div>

          {/* Right column: Performance Chart + News/Movers */}
          <div className="lg:col-span-7 space-y-6">
            <PortfolioChart />
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <PortfolioNewsFeed
                news={portfolioNews}
                hasPortfolio={hasPositions}
                isLoading={isLoadingNews}
              />
              <TopMovers positions={positions} />
            </div>
          </div>
        </div>

        {/* Holdings Table */}
        <HoldingsTable
          positions={positionsWithScan}
          sparklines={sparklines}
          onDeepDive={handleStartDeepDive}
        />

        {/* Deep Dive Drawer */}
        <DeepDiveDrawer
          drawerMode={drawerMode}
          onClose={handleCloseDrawer}
          onMinimize={handleMinimize}
          onAnalysisComplete={handleAnalysisComplete}
          onPhaseChange={setAnalysisPhase}
          ticker={deepDiveTicker || ''}
          position={deepDivePosition}
          savedResult={savedViewResult}
        />

        {/* Deep Dive Minimized Pill */}
        {drawerMode === 'minimized' && deepDiveTicker && (
          <DeepDiveMinimized
            ticker={deepDiveTicker}
            phase={analysisPhase === 'done' ? 'done' : analysisPhase === 'error' ? 'error' : 'running'}
            onExpand={handleExpandFromMinimized}
          />
        )}

      </div>
    </div>
  );
}
